import { useEffect, useState } from 'react';
import { CardModalFormNames, CardModalFormValues } from './types/CardModalFormNames';
import cardModalValidationSchema, {
  getCardUsageOptionsSchema,
  cardDetailsValidationSchema,
} from './types/CardModalValidationSchema';
import { getFavouritesDetails } from 'features/cards/helpers/add-edit-card-helper';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc';
import {
  useSelectAssignedGrandfatherClientNumber,
  useSelectAllAccounts,
  useSelectCardFormValues,
  useSelectCardModalMeta,
  useSelectCardOptions,
} from 'selectors/selectors';
import {
  useDispatchCardFormValues,
  useDispatchGetCardDefaults,
  useDispatchDisableExpiryDate,
} from 'dispatchers/dispatchers';
import { useAutofillSingleAccount } from './useAutofillSingleAccount';
import { gtmFormOnChangeEvents } from 'GTM';
import { getCardStatus } from 'utilities/card-utilities';
import { useDisableExpiryDate } from 'selectors/selectors';
import Env from 'Env';
import {
  capDailyLimitToCreditLimit,
  capDailyLimitToMaximumOption,
  filterDailyDollarLimitsByCreditLimit,
  filterRestrictedDailyDollarLimits,
  getEffectiveCreditLimit,
} from 'features/cards/helpers/card-limit-utilities';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Used for controlling generic functionality of the card modal.
 */
export default function useCardModalController(
  modalType?: string,
  id?: string,
  selectedAccountNumber?: string | string[]
) {
  const dispatchCardFormValues = useDispatchCardFormValues();
  const getCardDefaults = useDispatchGetCardDefaults();
  const dispatchDisableExpiryDate = useDispatchDisableExpiryDate();

  const meta = useSelectCardModalMeta();
  const assignedGrandfatherClientNumber = useSelectAssignedGrandfatherClientNumber();
  const cardOptions = useSelectCardOptions();
  const formValues = useSelectCardFormValues();
  const allAccounts = useSelectAllAccounts();
  const accountCreditLimit = getEffectiveCreditLimit(
    allAccounts,
    selectedAccountNumber ?? formValues?.[CardModalFormNames.Account]
  );
  const shouldFilterDailyLimits = typeof Env.getIsCardLimitsReadOnly === 'function' && Env.getIsCardLimitsReadOnly();
  const isRestrictedLimitFlow = modalType === 'newCard' || id === 'edit-card-modal-form';
  const allowedDailyDollarLimits = isRestrictedLimitFlow
    ? filterRestrictedDailyDollarLimits(meta?.dailyDollarLimits)
    : meta?.dailyDollarLimits;
  const availableDailyDollarLimits = shouldFilterDailyLimits
    ? filterDailyDollarLimitsByCreditLimit(allowedDailyDollarLimits, accountCreditLimit)
    : allowedDailyDollarLimits;
  const isStatusCardProcessing = getCardStatus() === 'Processing';
  const disableExpiryDate = useDisableExpiryDate();

  const isEdit = id && id === 'edit-card-modal-form' ? true : false;

  const [lastValueChanged, setLastValueChanged] = useState<{
    name: string;
    value: any;
    mode?: any;
  }>(null);

  const fleetLimitersCodes = meta?.fleetLimiters?.map((item) => item.code);
  const validationSchema = cardModalValidationSchema
    .concat(
      getCardUsageOptionsSchema(
        fleetLimitersCodes,
        cardOptions?.map((obj) => obj.favouriteName)
      )
    )
    .concat(cardDetailsValidationSchema(isEdit, disableExpiryDate));

  useAutofillSingleAccount();

  // Handle the last value change and update the form values accordingly. (Handles side effects like favourites, all products, etc.)
  useEffect(() => {
    if (lastValueChanged == null) return;

    const handleAccountChange = (contractNumber) => {
      getCardDefaults(contractNumber, assignedGrandfatherClientNumber);
    };

    const handleAllProductsChange = (allProducts, mode) => {
      const newFormValues = meta?.fleetLimiters?.reduce(
        (acc, option) => ({ ...acc, [option.code]: option.available ? allProducts : false }),
        {}
      );
      if (mode === 'edit') {
        window.dataLayer.push({
          event: 'Card_Portal_Individual_Card_Detail_Usage_Products_And_Services',
          services_selected: newFormValues,
        });
      }
      dispatchCardFormValues(newFormValues);
    };

    const handleFavouriteChange = (favourite) => {
      const favSelected = cardOptions?.find((card) => card.id === favourite);
      const favouriteDetails = getFavouritesDetails(
        favSelected,
        meta?.fleetLimiters,
        meta?.vasRsaAccountCode,
        meta?.vasSnrAccountCode
      );
      const newFormValues = {
        ...favouriteDetails,
        dailyLimit: capDailyLimitToMaximumOption(
          shouldFilterDailyLimits
            ? capDailyLimitToCreditLimit(favouriteDetails.dailyLimit, allowedDailyDollarLimits, accountCreditLimit)
            : favouriteDetails.dailyLimit,
          availableDailyDollarLimits
        )?.toString(),
      };
      // If the card is in processing status, we need to keep the odometer and order number required fields as they are.
      // Changing those fields will require a card to be reissued.
      if (isStatusCardProcessing) {
        newFormValues[CardModalFormNames.OdometerRequired] = formValues[CardModalFormNames.OdometerRequired];
        newFormValues[CardModalFormNames.OrderNumberRequired] = formValues[CardModalFormNames.OrderNumberRequired];
      }
      dispatchCardFormValues(newFormValues);
    };

    const handleExpiryDateChange = () => {
      dispatchDisableExpiryDate(false);
    };

    const fleetLimiterChange = () => {
      const isAllProductsChecked = meta?.fleetLimiters?.reduce((allChecked, item) => {
        return allChecked && formValues[item.code] === true;
      }, true);
      dispatchCardFormValues({
        [CardModalFormNames.AllProducts]: isAllProductsChecked,
      });
    };

    // check if any of the fleet limiters have changed
    if (meta?.fleetLimiters?.some((item) => item.code === lastValueChanged?.name)) {
      fleetLimiterChange();
    }

    switch (lastValueChanged?.name) {
      case CardModalFormNames.Account:
        handleAccountChange(lastValueChanged.value);
        break;
      case CardModalFormNames.AllProducts:
        handleAllProductsChange(lastValueChanged.value, lastValueChanged.mode);
        break;
      case CardModalFormNames.Favourite:
        handleFavouriteChange(lastValueChanged.value);
        break;
      case CardModalFormNames.IssueDateTo:
        handleExpiryDateChange();
        break;
      default:
    }
  }, [lastValueChanged]);

  function onFormChange(values: CardModalFormValues, name: string | undefined) {
    gtmFormOnChangeEvents(name ?? '', values, modalType);
    setLastValueChanged({ name: name ?? '', value: values[name ?? ''], mode: 'add' });
    dispatchCardFormValues(values);
  }

  return {
    defaultValues: formValues,
    onFormChange,
    validationSchema,
  };
}
